import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api';

/**
 * Hook host — le téléphone/PC enseignant contrôle un écran distant via un code.
 * Utilisation :
 *   const cast = useRvpCastHost(slug);
 *   cast.start(); // crée session, retourne le code
 *   cast.push({ current_slide: 3, reveal_map: {...} });
 *   cast.pushLaser({ x: 0.5, y: 0.5, visible: true });
 *   cast.close();
 */
export function useRvpCastHost(slug) {
  const [code, setCode] = useState(null);
  const [active, setActive] = useState(false);
  const [error, setError] = useState('');
  const codeRef = useRef(null);

  const start = useCallback(async () => {
    setError('');
    try {
      const r = await api.castCreate(slug);
      setCode(r.code);
      codeRef.current = r.code;
      setActive(true);
      return r.code;
    } catch (e) {
      setError(e?.response?.data?.detail || 'Impossible de démarrer la diffusion');
      return null;
    }
  }, [slug]);

  const push = useCallback(async (state) => {
    if (!codeRef.current) return;
    try {
      await api.castUpdate(codeRef.current, state);
    } catch (_) { /* silent — retry on next push */ }
  }, []);

  // Throttle laser updates (max ~15/sec) to éviter de saturer le serveur
  const laserThrottleRef = useRef(0);
  const pushLaser = useCallback((laser) => {
    if (!codeRef.current) return;
    const now = Date.now();
    if (now - laserThrottleRef.current < 66) return; // ~15 fps
    laserThrottleRef.current = now;
    api.castUpdate(codeRef.current, { laser }).catch(() => {});
  }, []);

  const sendCmd = useCallback((cmd) => {
    if (!codeRef.current) return;
    api.castUpdate(codeRef.current, { cmd }).catch(() => {});
  }, []);

  const close = useCallback(async () => {
    if (!codeRef.current) return;
    try { await api.castClose(codeRef.current); } catch (_) { /* silent */ }
    codeRef.current = null;
    setCode(null);
    setActive(false);
  }, []);

  useEffect(() => () => { if (codeRef.current) api.castClose(codeRef.current).catch(() => {}); }, []);

  return { code, active, error, start, push, pushLaser, sendCmd, close };
}

/**
 * Hook receiver — l'écran distant (vidéoprojecteur/TV/PC) reçoit le state en polling.
 */
export function useRvpCastReceiver(code, { interval = 800 } = {}) {
  const [state, setState] = useState(null);
  const [error, setError] = useState('');
  const [connected, setConnected] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!code) return undefined;
    let stopped = false;
    const poll = async () => {
      try {
        const s = await api.castGet(code);
        if (stopped) return;
        setState(s);
        setConnected(true);
        setError('');
      } catch (e) {
        if (stopped) return;
        setConnected(false);
        if (e?.response?.status === 410) setError('Session expirée (4h max)');
        else if (e?.response?.status === 404) setError('Session introuvable ou fermée');
        else setError('Connexion perdue…');
      }
    };
    poll();
    timerRef.current = setInterval(poll, interval);
    return () => { stopped = true; clearInterval(timerRef.current); };
  }, [code, interval]);

  return { state, error, connected };
}
